/**
 * 합계 검증 / 연동 계정 floating panel
 *
 * Figma:
 * - Design System 0.1 > IXD-DSD Reconciliation > Floating panel > 도메인 합계 검증
 * - Design System 0.1 > IXD-DSD Reconciliation > Floating panel > 연동 계정 안내
 *
 * 셀 메타 정보(같은 identity 가 등장하는 본문/주석 위치들)를 popover 로 보여주는
 * 패널. 두 가지 모드를 지원한다:
 *
 * - `sum-validation` (default): 합계 검증. "입력한 값 / 계산 결과" 두 행을 추가로
 *   노출하며 dark 헤더 + 경고 아이콘. autoSumValueDifferent 셀에서 사용.
 * - `linked-cells`: 연동 계정. 헤더와 출처 리스트만 노출. 일반 identity 연동 셀에서 사용.
 * - `modified`: 수정한 셀. 주황 '수정' 칩 헤더 + 기존 값/수정한 값만 노출.
 *
 * 연동 셀이 수정까지 된 경우 `modifiedValues` 를 넘기면 linked-cells 리스트
 * 하단에 기존 값/수정한 값 행이 함께 노출된다.
 *
 * 본문/주석 분류 + 항목 표시 포맷팅은 패널 내부에서 일관되게 처리한다 — 소비자는
 * 평면 리스트(`CellLinkageItem[]`) 만 넘기면 된다.
 */

import { Fragment, useMemo, useState } from "react";
import {
  CircleDashed,
  Copy,
  Info,
  Link2,
  TriangleAlert,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { isFinancialStatementChunkTitle } from "@/utils/ixd-editor/chunk-kind";
import ChipSolid from "@/toggle-ui-kit/chip/chip-solid";
import { Switch } from "@/toggle-ui-kit/control";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { DecorateInteraction } from "@/toggle-ui-kit/decorate/interaction";
import { toast } from "sonner";

/**
 * 출처 한 건. chunkTitle / dlViewTitle 은 결합하지 않고 별도 필드로 받아
 * 분류 / 표시 책임은 패널이 진다.
 *
 * `onClick` 이 있으면 해당 항목이 버튼으로 렌더링되어 클릭 시 호출된다.
 * (예: 대응 셀 위치로 포커스 이동). 없으면 일반 텍스트로 표시.
 */
export interface CellLinkageItem {
  chunkTitle: string;
  dlViewTitle: string;
  onClick?: () => void;
}

/**
 * 패널 내부에서 본문/주석으로 분류된 결과 구조. 외부에 직접 노출되진 않지만
 * 항목 별도 관리(`item: CellLinkageItem`) 계약을 명시한다.
 */
export interface CellLinkageSection {
  label: string;
  items: CellLinkageItem[];
}

export type CellLinkagePanelMode =
  | "sum-validation"
  | "linked-cells"
  | "modified"
  /** 값이 들어가지 않아 알릴 것만 있는 셀 */
  | "cell-notice";

/** 수정한 셀의 기존/수정 표시값 (셀과 동일한 포맷팅을 거친 문자열) */
export interface ModifiedCellDisplayValues {
  original: string;
  modified: string;
  /**
   * 연동 표들의 최저(기준) 단위로 환산한 값 — 셀 단위가 기준보다 높아 수정이
   * 반올림에 묻힐 수 있는 경우에만 존재. '단위 변환' 토글 ON 시 이 값을 표시.
   */
  baseUnit?: { original: string; modified: string } | null;
}

/**
 * 값이 들어가지 않은 셀에 붙는 안내.
 *
 * 패널은 문구를 그리기만 한다 — "충돌"이 무슨 뜻인지는 화면이 안다.
 */
export interface CellNotice {
  /** 셀 배경 강조. 없으면 강조하지 않는다 — 이미 처리된 항목 */
  tone?: "conflict" | "blocked";
  /** 안내 문구 */
  description: string;
}

export interface CellLinkageFloatingPanelProps {
  /** 패널 모드 — 기본값 `"sum-validation"` */
  mode?: CellLinkagePanelMode;
  /** [sum-validation 전용] 사용자가 입력한 값 — 강조 배경 */
  inputValue?: string;
  /** [sum-validation 전용] 시스템이 계산한 합계 */
  calculatedValue?: string;
  /** [sum-validation 전용] 계산 결과 옆 ⓘ 아이콘 hover 시 보조 설명 */
  calculationHint?: string;
  /** 수정한 셀의 기존 값/수정한 값 — linked-cells·modified 모드에서 하단에 노출 */
  modifiedValues?: ModifiedCellDisplayValues | null;
  /** 값이 들어가지 않은 이유와 선택지 — 어느 모드에서든 최상단에 노출 */
  notice?: CellNotice | null;
  /** 출처 위치 평면 리스트 — 패널이 본문/주석으로 분류한다 */
  items: CellLinkageItem[];
  onClose?: () => void;
  /** 위치/추가 스타일 (예: "fixed bottom-6 right-6 z-50") */
  className?: string;
}

// Figma Shadow/Large 정확 매핑
const SHADOW_LARGE =
  "shadow-[0_4px_8px_4px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.1)]";

const PANEL_ARIA_LABEL: Record<CellLinkagePanelMode, string> = {
  "cell-notice": "셀 값 확인",
  "sum-validation": "합계 검증",
  "linked-cells": "연동 계정",
  modified: "수정한 셀",
};

/** 단위 변환 토글 thumb 아이콘 (Figma 10×10) — 색상은 currentColor */
const UnitConvertIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    className={className}
    aria-hidden
  >
    <path
      d="M7.23356 5.56687C7.38001 5.42042 7.61772 5.42042 7.76416 5.56687L9.43042 7.23353C9.57686 7.37998 9.57686 7.61768 9.43042 7.76413L7.76416 9.4308C7.61772 9.57724 7.38001 9.57724 7.23356 9.4308C7.08712 9.28436 7.08712 9.04665 7.23356 8.9002L8.25977 7.87399H6.70703C6.03959 7.87397 5.37731 7.51842 5.01554 6.94992L5.01392 6.94748L4.80558 6.61423C4.69588 6.43861 4.74961 6.20722 4.92521 6.09747C5.10082 5.9878 5.33224 6.0411 5.44198 6.21669L5.64868 6.5475L5.69222 6.61138C5.92255 6.92329 6.31718 7.12364 6.70703 7.12367H8.25977L7.23356 6.09747C7.08712 5.95102 7.08712 5.71331 7.23356 5.56687ZM7.23356 0.566866C7.38001 0.42042 7.61771 0.42042 7.76416 0.566866L9.43083 2.23353C9.57727 2.37998 9.57727 2.61769 9.43083 2.76413L7.76416 4.4308C7.61771 4.57725 7.38001 4.57725 7.23356 4.4308C7.08711 4.28435 7.08711 4.04665 7.23356 3.9002L8.25977 2.874H6.70703C6.27098 2.87405 5.86015 3.07636 5.64868 3.40866C5.64533 3.41393 5.64171 3.41903 5.6381 3.42413L3.0966 7.0073L3.09375 7.01096C2.69038 7.5656 2.07811 7.87394 1.41569 7.87399H0.832194C0.625087 7.87399 0.457031 7.70594 0.457031 7.49883C0.457031 7.29173 0.625087 7.12367 0.832194 7.12367H1.41569C1.83646 7.12362 2.22383 6.93178 2.48706 6.56988L5.02612 2.99037C5.39991 2.41641 6.06565 2.12372 6.70703 2.12367H8.25977L7.23356 1.09747C7.08711 0.951018 7.08711 0.713313 7.23356 0.566866ZM1.62402 2.12367C2.39642 2.12373 3.10144 2.58331 3.45386 3.23776C3.55204 3.42009 3.48398 3.64737 3.30168 3.74558C3.11938 3.84374 2.8921 3.77563 2.79386 3.5934C2.56299 3.16463 2.10149 2.87406 1.62402 2.874H0.832194C0.625087 2.874 0.457031 2.70594 0.457031 2.49883C0.457031 2.29173 0.625087 2.12367 0.832194 2.12367H1.62402Z"
      fill="currentColor"
    />
  </svg>
);

// 부모 grid(grid-cols-[auto_1fr])의 한 행 — 라벨 열 폭을 두 행이 공유해
// 값의 왼쪽 끝이 표처럼 정렬된다. 행 배경은 좌/우 셀이 나눠 그린다.
const ModifiedValueRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <>
    <div className="whitespace-nowrap rounded-l bg-background-neutral-alternative p-1.5 pr-1 text-label-alternative caption-2-medium">
      {label}
    </div>
    <div className="min-w-0 truncate rounded-r bg-background-neutral-alternative py-1.5 pr-1.5 text-label-strong caption-2-medium">
      {value || "\u00A0"}
    </div>
  </>
);

const CellLinkageFloatingPanel = ({
  mode = "sum-validation",
  inputValue,
  calculatedValue,
  calculationHint,
  modifiedValues,
  notice,
  items,
  onClose,
  className,
}: CellLinkageFloatingPanelProps) => {
  const sections = useMemo<CellLinkageSection[]>(() => {
    const bodyItems: CellLinkageItem[] = [];
    const noteItems: CellLinkageItem[] = [];
    for (const item of items) {
      if (isFinancialStatementChunkTitle(item.chunkTitle)) {
        bodyItems.push(item);
      } else {
        noteItems.push(item);
      }
    }
    return [
      ...(bodyItems.length > 0 ? [{ label: "본문", items: bodyItems }] : []),
      ...(noteItems.length > 0 ? [{ label: "주석", items: noteItems }] : []),
    ];
  }, [items]);

  const isSumValidation = mode === "sum-validation";

  // 단위 변환 토글 — OFF(기본)면 셀 단위 그대로, ON이면 기준(최저) 단위 환산값.
  const [showBaseUnit, setShowBaseUnit] = useState(false);
  const displayedValues =
    showBaseUnit && modifiedValues?.baseUnit
      ? modifiedValues.baseUnit
      : modifiedValues;

  return (
    <div
      role="dialog"
      aria-label={PANEL_ARIA_LABEL[mode]}
      className={cn(
        "w-[220px] overflow-hidden rounded-[8px] bg-background-base border-line-neutral border",
        SHADOW_LARGE,
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start self-stretch border-b border-line-neutral">
        <div className="flex flex-1 py-2 px-1.5">
          {isSumValidation ? (
            <ChipSolid
              size="18px"
              leftIcon={<TriangleAlert size={10} />}
              rounded={false}
              showRightIcon={false}
              className="bg-primary-base text-static-white"
            >
              합계 검증
            </ChipSolid>
          ) : mode === "cell-notice" ? (
            // 셀에 확인할 알림이 있다(값이 안 들어갔거나, 들어간 값을 확인해야 한다).
            // 연동·수정 여부보다 우선한다 — 워크북 셀에서도 같은 패널을 쓰므로
            // 여기서 '연동 계정'이라 하면 틀린 말이 된다.
            // 들어간 값을 확인하라는 알림(conflict)은 막힌 것이 아니라 노란색으로 낮춘다.
            <ChipSolid
              size="18px"
              leftIcon={<TriangleAlert size={10} />}
              rounded={false}
              showRightIcon={false}
              className={cn(
                notice?.tone === "conflict" ? "bg-amber-500" : "bg-red-500",
                "text-static-white",
              )}
            >
              확인 필요
            </ChipSolid>
          ) : mode === "modified" ? (
            <ChipSolid
              size="18px"
              leftIcon={<CircleDashed size={10} />}
              rounded={false}
              showRightIcon={false}
              className="bg-orange-500 text-static-white"
            >
              수정
            </ChipSolid>
          ) : (
            <ChipSolid
              size="18px"
              leftIcon={<Link2 size={10} />}
              rounded={false}
              showRightIcon={false}
              className="bg-green-600 text-static-white"
            >
              연동 계정
            </ChipSolid>
          )}
        </div>
        {onClose && (
          <div className="flex items-center justify-center h-[34px] px-1.5">
            <IconButton icon={<X />} onClick={onClose} aria-label="닫기" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 py-2 px-1.5">
        {/* 값이 왜 안 들어갔는지가 가장 먼저다 — 연동 위치보다 위에 둔다. */}
        {notice && (
          <div className="flex flex-col gap-2">
            <span
              className={cn(
                "caption-2-medium",
                notice.tone === "conflict" ? "text-purple-600" : "text-red-500",
              )}
            >
              {notice.description}
            </span>
            {(sections.length > 0 || modifiedValues) && (
              <div aria-hidden className="mx-1 border-t border-line-neutral" />
            )}
          </div>
        )}
        {isSumValidation && (
          <div className="flex flex-col gap-2">
            <div className="flex p-1.5 items-center gap-1 self-stretch rounded bg-background-neutral-alternative">
              <div className="text-label-alternative caption-2-medium">
                입력한 값
              </div>
              <div className="text-label-strong caption-2-medium">
                {inputValue ?? ""}
              </div>
            </div>
            <div className="flex p-1.5 items-center gap-1 self-stretch rounded bg-background-neutral-alternative">
              <div className="text-label-alternative caption-2-medium">
                계산 결과
              </div>
              <div className="text-label-strong caption-2-medium flex-1 flex items-center gap-2">
                {calculatedValue ?? ""}
                <DecorateInteraction
                  type="button"
                  variant="light"
                  color="blue"
                  className="rounded p-0.5"
                  aria-label="계산 결과 복사"
                  onClick={(event) => {
                    void navigator.clipboard
                      ?.writeText(calculatedValue ?? "")
                      .catch(() => {});
                    // 복사 후 버튼 포커스를 떼어 focus-within 오버레이가 남지 않게 한다.
                    event.currentTarget.blur();
                    toast.success("계산 결과가 복사되었습니다.");
                  }}
                >
                  <div>
                    <Copy className="size-3.5 shrink-0 text-label-alternative" />
                  </div>
                </DecorateInteraction>
              </div>
              <Tooltip
                label={
                  calculationHint ??
                  "입력한 값과 시스템 계산 결과가 달라 확인이 필요합니다."
                }
                side="left"
                align="center"
                contentClassName="max-w-[320px]"
              >
                <Info
                  className="size-3 shrink-0 text-label-alternative"
                  aria-label={calculationHint}
                  aria-hidden={calculationHint ? undefined : true}
                />
              </Tooltip>
            </div>
          </div>
        )}
        {sections.length > 0 && (
          <div>
            {/* sum-validation: 2-column grid (label | items).
              linked-cells: section label 위에, items 아래에 (vertical stack). */}
            <dl
              className={cn(
                "px-1.5 caption-2-medium",
                isSumValidation
                  ? "grid grid-cols-[auto_1fr] gap-x-3 gap-y-2"
                  : "flex flex-col gap-2",
              )}
            >
              {sections.map((section) => (
                <Fragment key={section.label}>
                  <dt className="text-label-alternative">{section.label}</dt>
                  <dd className="min-w-0">
                    <ul className="flex flex-col gap-0.5 text-label-normal">
                      {section.items.map((item, idx) => {
                        const itemText =
                          section.label === "본문"
                            ? item.chunkTitle
                            : `${item.chunkTitle}: ${item.dlViewTitle}`;
                        return (
                          <li key={`${section.label}-${idx}`}>
                            {item.onClick ? (
                              <DecorateInteraction
                                asChild
                                variant="light"
                                color="neutral"
                              >
                                <button
                                  type="button"
                                  onClick={item.onClick}
                                  className="flex w-full min-w-0 cursor-pointer gap-1 rounded-[2px] px-1 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2b7fff]"
                                >
                                  <span aria-hidden>•</span>
                                  <span className="min-w-0 [overflow-wrap:anywhere]">
                                    {itemText}
                                  </span>
                                </button>
                              </DecorateInteraction>
                            ) : (
                              <div className="flex min-w-0 gap-1 px-1 py-0.5">
                                <span aria-hidden>•</span>
                                <span className="min-w-0 [overflow-wrap:anywhere]">
                                  {itemText}
                                </span>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </dd>
                </Fragment>
              ))}
            </dl>
          </div>
        )}
        {/* 수정한 셀: 기존 값/수정한 값. 연동 리스트가 있으면 구분선 아래에 붙는다. */}
        {!isSumValidation && modifiedValues && displayedValues && (
          <div className="flex flex-col gap-2">
            {sections.length > 0 && (
              <div aria-hidden className="mx-1 border-t border-line-neutral" />
            )}
            {modifiedValues.baseUnit && (
              <div className="flex items-center justify-end gap-1">
                <span className="caption-2-medium text-label-alternative">
                  단위 변환
                </span>
                <Switch
                  size={24}
                  active={showBaseUnit}
                  icon={
                    <UnitConvertIcon
                      // ON: track과 같은 진한 주황(#ff6900), OFF: 기본 잉크색
                      className={
                        showBaseUnit ? "text-[#ff6900]" : "text-[#171717]"
                      }
                    />
                  }
                  onChange={setShowBaseUnit}
                  aria-label="단위 변환"
                />
              </div>
            )}
            <div className="grid grid-cols-[auto_1fr] gap-y-2">
              <ModifiedValueRow
                label="기존 값"
                value={displayedValues.original}
              />
              <ModifiedValueRow
                label="수정한 값"
                value={displayedValues.modified}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CellLinkageFloatingPanel;
